<template>
  <v-container>
    <v-card class="px-6 py-6">
      <div v-if="!loadingProject">
        <h1>{{ project.name }}</h1>
        <v-spacer></v-spacer>
        <a :href="`https://${project.domain}`" target="_blank"
          >{{ project.domain }}
          <v-icon class="ml-1" small> mdi-open-in-new </v-icon>
        </a>
      </div>
      <div v-if="loadingProject" class="my-5 d-flex justify-center">
        <v-progress-circular class="px-8" indeterminate></v-progress-circular>
        <span> Loading project.. </span>
      </div>
      <transition name="stf-fade">
        <div v-if="!loadingProject" class="project-info">
          <div class="mt-2 mb-4">
            <v-chip
              v-if="project.status"
              :class="{
                flat: !isFailStatus,
                red: isFailStatus,
                green: isSuccessStatus,
              }"
              label
              small
            >
              <p class="status-chip mt-4 display-block">
                <v-tooltip v-if="isFailStatus || isInitializingStatus" right>
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      :class="{ 'text-white': isFailStatus }"
                      v-on="on"
                      v-bind="attrs"
                      style="cursor: pointer"
                    >
                      {{ project.status }}
                    </span>
                  </template>
                  <span v-if="isFailStatus">
                    An error occured:<br />
                    {{ project.error_message }}
                  </span>
                  <span v-else-if="isInitializingStatus">
                    We're fetching your keywords, hang tight!
                  </span>
                </v-tooltip>
                <span
                  v-else
                  :class="{ 'text-white': isFailStatus }"
                  style="cursor: pointer"
                >
                  {{ project.status }}
                </span>
              </p>
            </v-chip>
            <v-btn
              v-if="isFailStatus"
              @click="retryProject"
              title="Retry Project"
              class="mx-2"
              x-small
            >
              <v-icon small> mdi-refresh </v-icon>
            </v-btn>
          </div>
          <v-row>
            <v-col cols="6">
              <p>
                <b>Description</b><br />
                {{ project.description }}
              </p>
            </v-col>
            <v-col cols="6">
              <p>
                <b>Target audience:</b><br />
                {{ project.target_audience || 'No target audience set' }} -
                {{ project.target_audience_geographical_area }}
              </p>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="3">
              <p v-if="project.seeds && project.seeds.length > 0">
                <span class="font-weight-black ml-2">Seeds:</span><br />
                <v-chip
                  v-for="seed in project.seeds"
                  :key="seed"
                  class="ma-1"
                  color="primary"
                  outlined
                  x-small
                >
                  {{ seed }}
                </v-chip>
              </p>
              <p v-else>No seeds set</p>
            </v-col>
          </v-row>
          <div v-if="!rows">No project data available.</div>
          <p class="text-sm">
            <a @click="$router.back()" href="javascript:void(0)">
              Back to Previous Page
            </a>
          </p>
          <v-divider class="my-4"></v-divider>
          <v-row>
            <v-col cols="6">
              <div class="mb-1">
                <b>Major Groups</b>
                <v-btn
                  v-if="active_major_group"
                  @click="clearMajorGroup"
                  class="btn-clear-group ma-1 ml-3"
                  outlined
                  rounded
                  small
                >
                  Clear Major Group
                </v-btn>
              </div>
              <div
                v-if="
                  project.project_data &&
                  Object.keys(project.project_data).length > 0
                "
                class="d-flex flex-wrap gap-2"
              >
                <v-btn
                  v-for="major_group in Object.keys(project.project_data)"
                  @click="
                    rows = prepareMajorGroup(
                      project.project_data[major_group],
                      major_group,
                      true
                    )
                  "
                  :key="major_group"
                  class="ma-1 flex-column"
                  :class="{
                    'active-cluster': active_major_group === major_group,
                  }"
                  color="primary"
                  outlined
                  rounded
                  small
                >
                  {{ major_group }}
                  (
                  <div class="align-center">
                    <span class="text-xxs">
                      {{ groupSizes.find(g => g.group === major_group).size }}
                    </span>
                  </div>
                  )
                </v-btn>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="mb-1">
                <b>Minor Groups</b>
                <v-btn
                  v-if="active_minor_group"
                  @click="clearMinorGroup"
                  class="btn-clear-group ma-1"
                  color="
                    grey lighten-1
                  "
                  outlined
                  rounded
                  small
                >
                  Clear Minor Group
                </v-btn>
              </div>
              <div
                v-if="
                  active_major_group &&
                  Object.keys(project.project_data[active_major_group]).length >
                    0
                "
                class="d-flex flex-wrap gap-2"
              >
                <v-btn
                  v-for="minor_group in Object.keys(
                    project.project_data[active_major_group]
                  )"
                  @click="
                    rows = prepareMinorGroup(
                      project.project_data[active_major_group][minor_group],
                      minor_group
                    )
                  "
                  :key="minor_group"
                  class="ma-1"
                  :class="{
                    'active-cluster': active_minor_group === minor_group,
                  }"
                  color="secondary"
                  outlined
                  rounded
                  small
                >
                  {{ minor_group }} (
                  <div class="align-center">
                    <span class="text-xxs text-bold">
                      {{
                        project.project_data[active_major_group][minor_group]
                          .length
                      }}
                    </span>
                  </div>
                  )
                </v-btn>
              </div>
            </v-col>
          </v-row>
          <v-data-table
            v-if="rows"
            :headers="headers"
            :items="filteredRows"
            :search="search"
            :items-per-page="25"
            :footer-props="{
              'items-per-page-options': [10, 25, 100, -1],
              'items-per-page-text': 'Keywords per page',
              'show-first-last-page': true,
            }"
            :loading="loadingProject"
            loading-text="Loading... Please wait"
            multi-sort
            :sort-by="['avg_monthly_searches', 'competition']"
            :sort-desc="[true, false]"
            class="elevation-1 compact-table mt-4"
            dense
          >
            <template v-slot:top>
              <v-row class="mx-4">
                <v-col cols="3">
                  <v-text-field
                    v-model.number="competitionFilter"
                    label="Max Competition"
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    dense
                    outlined
                    clearable
                  />
                </v-col>
                <v-col cols="7">
                  <v-text-field
                    v-model="search"
                    label="Search"
                    dense
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="2" class="d-flex justify-end">
                  <v-btn @click="downloadCSV" small>
                    <v-icon left>mdi-download</v-icon>
                    <span>Download CSV</span>
                  </v-btn>
                </v-col>
              </v-row>
            </template>
            <template v-slot:item.score="{ item }">
              {{ item.score.toFixed(2) }}
            </template>
            <template v-slot:footer>
              <div v-if="filteredCount > 0" class="d-flex justify-end pa-2">
                <span class="grey--text text--darken-1 text-xs">
                  ({{ filteredCount }} keyword{{
                    filteredCount === 1 ? '' : 's'
                  }}
                  hidden by filter)
                </span>
              </div>
            </template>
          </v-data-table>
        </div>
      </transition>
    </v-card>
  </v-container>
</template>

<script>
  import { slugify } from '@/util/helpers.js'
  export default {
    data() {
      return {
        loadingProject: true,
        project: {
          project_data: {},
        },
        rows: [],
        headers: [
          { text: 'Keyword', value: 'keyword' },
          { text: 'Major Group', value: 'major_group' },
          { text: 'Minor Group', value: 'minor_group' },
          {
            text: 'Avg Monthly Searches (G Ads)',
            value: 'avg_monthly_searches',
          },
          { text: 'Competition (G Ads)', value: 'competition_index' },
          { text: 'Seed Similarity', value: 'score' },
          // { text: 'Search Volume (Semrush)', value: 'semrush_search_volume' },
        ],
        active_major_group: '',
        active_minor_group: '',
        search: '',
        competitionFilter: null,
      }
    },
    computed: {
      isFailStatus() {
        return (
          typeof this.project.status === 'string' &&
          this.project.status.toLowerCase().includes('fail')
        )
      },
      isSuccessStatus() {
        const success_states = ['keywords_clustered', 'completed']
        return success_states.includes(this.project.status)
      },
      isInitializingStatus() {
        const initializing_states = ['initialized', 'retry-initialization']
        return initializing_states.includes(this.project.status)
      },
      filteredRows() {
        if (!this.competitionFilter && this.competitionFilter !== 0)
          return this.rows
        return this.rows.filter(
          row => Number(row.competition_index) <= this.competitionFilter
        )
      },
      filteredCount() {
        return this.rows.length - this.filteredRows.length
      },
      groupSizes() {
        return Object.keys(this.project.project_data).map(group => {
          return {
            group,
            size: Object.values(this.project.project_data[group]).reduce(
              (acc, group) => acc + group.length,
              0
            ),
          }
        })
      },
    },
    created() {
      this.getProjectData()
    },
    methods: {
      getProjectData() {
        this.apiGetProjectData(this.$route.params.project_id)
          .then(data => {
            if (data) {
              this.project = data
              this.prepareData(this.project.project_data)
            } else {
              console.log('ERROR! ' + data.errorMessage)
            }
          })
          .catch(err => {
            if (this.$env === 'dev') {
              console.log('API catch error: ' + err)
            }
          })
          .then(() => {
            this.loadingProject = false
          })
      },

      prepareMajorGroup(major_group, label, setActive = true) {
        // Prepare major groups
        let result = []
        for (const minor_group in major_group) {
          major_group[minor_group].forEach(item => {
            const keyword = Object.keys(item)[0]
            const details = item[keyword]
            result.push({
              keyword,
              major_group: label,
              minor_group: minor_group,
              score: details.score,
              competition_index: details.competition_index,
              search_volume: details.search_volume,
              avg_monthly_searches: details.avg_monthly_searches,
            })
          })
        }
        if (setActive) {
          this.active_major_group = label
        }
        return result
      },

      prepareMinorGroup(minor_group, label) {
        let result = []
        minor_group.forEach(item => {
          const keyword = Object.keys(item)[0]
          const details = item[keyword]
          result.push({
            keyword,
            major_group: this.active_major_group,
            minor_group: label,
            score: details.score,
            competition_index: details.competition_index,
            search_volume: details.search_volume,
            avg_monthly_searches: details.avg_monthly_searches,
          })
        })
        this.active_minor_group = label
        return result
      },

      prepareData(projectData) {
        this.rows = []
        for (const major_group in projectData) {
          this.rows = this.rows.concat(
            this.prepareMajorGroup(projectData[major_group], major_group, false)
          )
        }
      },
      clearMajorGroup() {
        this.prepareData(this.project.project_data) // Reset rows to show all data
        this.active_major_group = ''
        this.active_minor_group = ''
      },
      clearMinorGroup() {
        this.active_minor_group = ''
        // Show all rows for the current major group
        if (this.active_major_group) {
          this.rows = this.prepareMajorGroup(
            this.project.project_data[this.active_major_group],
            this.active_major_group,
            true
          )
        }
      },
      downloadCSV() {
        const rows = this.filteredRows
        if (!rows.length) return

        const columns = this.headers.map(h => h.value)
        const headerLabels = this.headers.map(h => h.text)

        let csv = headerLabels.join(',') + '\n'

        csv += rows
          .map(row =>
            columns
              .map(col => {
                let val = row[col]
                // Format score to 2 decimals
                if (col === 'score' && typeof val === 'number') {
                  val = val.toFixed(2)
                }
                // Escape quotes
                if (typeof val === 'string' && val.includes('"')) {
                  val = val.replace(/"/g, '""')
                }
                // Wrap in quotes if needed
                if (
                  typeof val === 'string' &&
                  (val.includes(',') || val.includes('"'))
                ) {
                  val = `"${val}"`
                }
                return val
              })
              .join(',')
          )
          .join('\n')

        const blob = new Blob([csv], { type: 'text/csv' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `project-${slugify(this.project.name)}.csv`
        console.log('Downloading CSV:', link.download)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      },
      retryProject() {
        this.loadingProject = true
        this.apiRetryProject(this.$route.params.project_id)
          .then(data => {
            this.getProjectData()
          })
          .catch(err => {
            console.error('Error reinitializing project:', err)
          })
          .finally(() => {
            this.loadingProject = false
          })
      },
    },
  }
</script>

<style scoped>
  .status-chip {
    font-size: 0.9em;
    margin-left: 0;
  }

  /* Make status chips only take up content size */
  .v-chip {
    width: fit-content !important;
    display: inline-block !important;
  }

  .active-cluster {
    background-color: #1976d2 !important;
    color: #fff !important;
    border-color: #1976d2 !important;
  }

  /* Optional: add a little gap between buttons if not using gap-2 utility */
  .d-flex.gap-2 > * {
    margin-right: 8px;
    margin-bottom: 8px;
  }

  .compact-table.v-data-table::v-deep td {
    font-size: 0.73em;
  }

  .btn-clear-group {
    font-size: 0.6em;
    padding: 0 8px;
  }
</style>
